import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import {
  FaFileAlt,
  FaClock,
  FaCheckCircle,
  FaShoppingCart,
  FaBoxOpen,
  FaSearch,
  FaEye,
  FaTimes,
  FaRedo,
  FaDownload,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaClipboardCheck,
  FaMoneyCheckAlt,
  FaTools,
  FaExclamationTriangle,
  FaBell,
  FaHistory,
  FaPaperclip,
  FaCalendarAlt,
  FaInfoCircle,
} from "react-icons/fa";

const API_BASE = "https://railsetu.in:5000/store/procurements";

/* =========================================================
   PROCUREMENT STAGES
========================================================= */

const STAGES = [
  { key: "REQUIREMENT", label: "Requirement", icon: FaFileAlt },
  { key: "APPROVAL", label: "Approval", icon: FaClipboardCheck },
  { key: "APPROVED", label: "Approved", icon: FaCheckCircle },
  { key: "ACCOUNTS", label: "Accounts", icon: FaMoneyCheckAlt },
  { key: "FINANCE_SANCTION", label: "Finance Sanction", icon: FaMoneyCheckAlt },
  {
    key: "FINANCE_CONCURRENCE",
    label: "Finance Concurrence",
    icon: FaMoneyCheckAlt,
  },
  { key: "DEMAND", label: "Demand", icon: FaFileAlt },
  {
    key: "FUND_AVAILABILITY",
    label: "Fund Availability",
    icon: FaMoneyCheckAlt,
  },
  { key: "UDM", label: "UDM", icon: FaFileAlt },
  { key: "FUND_VETTING", label: "Fund Vetting", icon: FaClipboardCheck },
  { key: "SANCTION", label: "Sanction", icon: FaCheckCircle },
  { key: "STORE", label: "Store", icon: FaBoxOpen },
  { key: "TPI", label: "TPI", icon: FaTools },
  { key: "TS_RR", label: "TS&RR", icon: FaTools },
  { key: "PAYMENT", label: "Payment", icon: FaMoneyCheckAlt },
  { key: "COMPLETED", label: "Completed", icon: FaCheckCircle },
  { key: "REJECTED", label: "Rejected", icon: FaExclamationTriangle },
  { key: "NOT_COMPLETED", label: "Not Completed", icon: FaExclamationTriangle },
  { key: "OTHER", label: "Other", icon: FaTools },
];

/* =========================================================
   HELPERS
========================================================= */

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return value;
};

const formatDate = (date) => {
  if (!date) return "—";

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return "—";

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeStage = (stage) => {
  if (!stage) return "";
  const value = String(stage).trim().toUpperCase().replaceAll(" ", "_");
  const aliases = {
    // Legacy API values mapped to the closest supported timeline stage.
    PURCHASE_ORDER: "STORE",
    INSPECTION: "TPI",
    RECEIPT: "STORE",
    FINANCE: "FINANCE_SANCTION",
    PURCHASE: "STORE",
  };
  return aliases[value] || value;
};

const getStageLabel = (status) => {
  const normalized = normalizeStage(status);

  const stage = STAGES.find((item) => item.key === normalized);

  if (stage) {
    return stage.label;
  }

  if (!status) {
    return "Unknown";
  }

  return String(status)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
const getOverallStatus = (item) => {
  // Latest history entry se current stage lo
  const latestStage = getCurrentStage(item);

  // Latest stage final completion ho
  if (latestStage === "COMPLETED") {
    return "Completed";
  }

  // Latest stage rejected ho, tabhi Rejected dikhao
  if (latestStage === "REJECTED") {
    return "Rejected";
  }

  // Latest stage receipt ho
  if (latestStage === "STORE" || latestStage === "RECEIPT") {
    return "Received";
  }

  if (latestStage === "NOT_COMPLETED") {
    return "Not Completed";
  }

  // Baaki stages mein case abhi process mein hai
  return "In Progress";
};

/*
   Determine which stage a case is currently in.
*/

const getCurrentStage = (item) => {
  const history = Array.isArray(item?.history) ? item.history : [];
  let latestEntry = null;
  let latestTimestamp = -1;
  let latestHistoryId = -1;

  // Single pass: find the newest history entry without copying/sorting the array.
  for (const entry of history) {
    const normalizedStage = normalizeStage(entry?.status_stage);
    if (!normalizedStage) continue;

    const timestamp =
      new Date(entry.action_date || entry.created_at || 0).getTime() || 0;
    const historyId = Number(entry.history_id || 0);

    if (
      timestamp > latestTimestamp ||
      (timestamp === latestTimestamp && historyId > latestHistoryId)
    ) {
      latestEntry = entry;
      latestTimestamp = timestamp;
      latestHistoryId = historyId;
    }
  }

  if (latestEntry) return normalizeStage(latestEntry.status_stage);
  const status = normalizeStage(item?.current_status);
  return status || "ACCOUNTS";
};

/* =========================================================
   COMPONENT
========================================================= */

function ProcurementOverview() {
  const [cases, setCases] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [accountFilter, setAccountFilter] = useState("All");

  const [stageFilter, setStageFilter] = useState("All");

  const [pendingFilter, setPendingFilter] = useState("All");

  const [statusFilter, setStatusFilter] = useState("All");

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState("");

  /*
     Selected case on MAIN dashboard.
  */

  const [selectedCase, setSelectedCase] = useState(null);

  /*
     Whether full-screen detail popup is open.
  */

  const [showDetails, setShowDetails] = useState(false);

  /*
     Stage selected inside detail popup.
  */

  const [selectedStage, setSelectedStage] = useState(null);

  // Used to keep the current stage visible when the detail popup opens.
  const timelineContainerRef = useRef(null);
  const currentStageItemRef = useRef(null);
  const stageDetailsRef = useRef(null);

  const [detailLoading, setDetailLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 7;

  /* =====================================================
     GET ALL CASES
  ===================================================== */

  const fetchProcurements = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(API_BASE);

      console.log("PROCUREMENT LIST:", response.data);

      setCases(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (err) {
      console.error("PROCUREMENT API ERROR:", err);

      setError(
        err.response?.data?.message || "Unable to load procurement data.",
      );

      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcurements();
  }, []);

  /* =====================================================
     GET PARTICULAR CASE
  ===================================================== */

  const selectCase = async (caseId, openPopup = false) => {
    try {
      setDetailLoading(true);

      const response = await axios.get(`${API_BASE}/${caseId}`);

      console.log(`CASE ${caseId}:`, response.data);

      const detail = response.data?.data?.[0];

      if (!detail) {
        return;
      }

      setSelectedCase(detail);

      /*
         Current stage automatically selected.
      */

      setSelectedStage(getCurrentStage(detail));

      if (openPopup) {
        setShowDetails(true);
      }
    } catch (err) {
      console.error("CASE DETAIL ERROR:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  /* =====================================================
     ACCOUNT TYPES
  ===================================================== */

  const accountTypes = useMemo(() => {
    return [
      "All",
      ...new Set(cases.map((item) => item.account_type).filter(Boolean)),
    ];
  }, [cases]);

  /* =====================================================
     FILTER
  ===================================================== */

  const filteredCases = useMemo(() => {
    return cases
      .filter((item) => {
        const text = search.toLowerCase().trim();

        const matchesSearch =
          !text ||
          String(item.case_id || "")
            .toLowerCase()
            .includes(text) ||
          String(item.item_name || "")
            .toLowerCase()
            .includes(text) ||
          String(item.file_number || "")
            .toLowerCase()
            .includes(text) ||
          String(item.e_office_number || "")
            .toLowerCase()
            .includes(text) ||
          String(item.demand_number || "")
            .toLowerCase()
            .includes(text) ||
          String(item.purchase_order_number || "")
            .toLowerCase()
            .includes(text);

        const matchesAccount =
          accountFilter === "All" || item.account_type === accountFilter;

        const matchesStage =
          stageFilter === "All" ||
          normalizeStage(item.current_status) === stageFilter;

        const matchesStatus =
          statusFilter === "All" || getOverallStatus(item) === statusFilter;

        const pendingDays = Number(item.pending_days || 0);

        let matchesPending = true;

        if (pendingFilter === "0") {
          matchesPending = pendingDays === 0;
        }

        if (pendingFilter === "1-7") {
          matchesPending = pendingDays >= 1 && pendingDays <= 7;
        }

        if (pendingFilter === "8-15") {
          matchesPending = pendingDays >= 8 && pendingDays <= 15;
        }

        if (pendingFilter === "15+") {
          matchesPending = pendingDays > 15;
        }

        let matchesDate = true;

        if (fromDate && item.demand_date) {
          matchesDate =
            new Date(item.demand_date) >= new Date(`${fromDate}T00:00:00`);
        }

        if (toDate && item.demand_date) {
          matchesDate =
            matchesDate &&
            new Date(item.demand_date) <= new Date(`${toDate}T23:59:59`);
        }

        return (
          matchesSearch &&
          matchesAccount &&
          matchesStage &&
          matchesStatus &&
          matchesPending &&
          matchesDate
        );
      })
      .sort((a, b) => {
        // Most recently inserted/updated case first.
        // Prefer the last update/action timestamp, then creation date.
        const getLatestTimestamp = (item) => {
          const historyTimes = (Array.isArray(item.history) ? item.history : [])
            .map((entry) =>
              new Date(entry.action_date || entry.created_at || 0).getTime(),
            )
            .filter((time) => Number.isFinite(time) && time > 0);
          const candidates = [
            item.updated_at,
            item.last_action_date,
            item.created_at,
            ...historyTimes,
          ]
            .map((value) =>
              typeof value === "number"
                ? value
                : new Date(value || 0).getTime(),
            )
            .filter((time) => Number.isFinite(time) && time > 0);
          return candidates.length ? Math.max(...candidates) : 0;
        };

        const timeDifference = getLatestTimestamp(b) - getLatestTimestamp(a);
        return (
          timeDifference || Number(b.case_id || 0) - Number(a.case_id || 0)
        );
      });
  }, [
    cases,
    search,
    accountFilter,
    stageFilter,
    statusFilter,
    pendingFilter,
    fromDate,
    toDate,
  ]);

  /* =====================================================
     SUMMARY
  ===================================================== */

  // Build KPI counts and hover-preview data in one pass over the cases.
  // Each case's current stage is calculated only once per cases update.
  const stageData = useMemo(() => {
    const counts = Object.fromEntries(STAGES.map((stage) => [stage.key, 0]));
    const previewItemsByStage = Object.fromEntries(
      STAGES.map((stage) => [stage.key, []]),
    );

    cases.forEach((item) => {
      const stageKey = getCurrentStage(item);
      if (!Object.prototype.hasOwnProperty.call(counts, stageKey)) return;

      counts[stageKey] += 1;
      previewItemsByStage[stageKey].push({
        caseId: item.case_id,
        itemName: item.item_name,
        pendingSince: item.pending_since,
        pendingDays: item.pending_days,
        _sortTime:
          new Date(item.pending_since || item.created_at || 0).getTime() || 0,
      });
    });

    Object.keys(previewItemsByStage).forEach((stageKey) => {
      previewItemsByStage[stageKey] = previewItemsByStage[stageKey]
        .sort((a, b) => a._sortTime - b._sortTime)
        .map(({ _sortTime, ...item }) => item);
    });

    return { counts, previewItemsByStage };
  }, [cases]);

  const stageCounts = stageData.counts;

  const openKpiCase = (stageKey) => {
    const matchingCase = cases.find((item) => {
      if (stageKey === "ALL") return true;
      return getCurrentStage(item) === stageKey;
    });
    if (!matchingCase) return;
    selectCase(matchingCase.case_id, true);
  };

  // Preview arrays are already prepared by stageData; hover only displays them.
  // Show only the 5 oldest-pending cases in the hover preview.
  const getKpiPreviewItems = (stageKey) =>
    (stageData.previewItemsByStage[stageKey] || []).slice(0, 5);

  const summary = {
    total: cases.length,
    ...Object.fromEntries(
      STAGES.map((stage) => [stage.key, stageCounts[stage.key] || 0]),
    ),
  };

  // Overall-status counts use the same function as the table Status column.
  const overallStatusCounts = useMemo(() => {
    const counts = { "In Progress": 0, Completed: 0, Rejected: 0 };
    cases.forEach((item) => {
      const status = getOverallStatus(item);
      if (Object.prototype.hasOwnProperty.call(counts, status)) {
        counts[status] += 1;
      }
    });
    return counts;
  }, [cases]);

  const handleOverallStatusFilter = (status) => {
    setStatusFilter((current) => (current === status ? "All" : status));
    setCurrentPage(1);
  };

  /* =====================================================
     PAGINATION
  ===================================================== */

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCases.length / itemsPerPage),
  );

  const visibleCases = filteredCases.slice(
    (currentPage - 1) * itemsPerPage,

    currentPage * itemsPerPage,
  );

  /* =====================================================
     RESET
  ===================================================== */

  const resetFilters = () => {
    setSearch("");
    setAccountFilter("All");
    setStageFilter("All");
    setStatusFilter("All");
    setPendingFilter("All");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  /* =====================================================
     EXPORT
  ===================================================== */

  const exportCSV = () => {
    const headers = [
      "Case ID",
      "File Number",
      "E-Office Number",
      "Demand Number",
      "Item",
      "Quantity",
      "Unit",
      "Account Type",
      "Demand Date",
      "Current Status",
      "Pending Days",
      "Payment Status",
      "Last Action Date",
      "Last Action Remarks",
    ];

    const rows = filteredCases.map((item) => [
      item.case_id,

      item.file_number || "",

      item.e_office_number || "",

      item.demand_number || "",

      item.item_name || "",

      item.quantity || "",

      item.unit || "",

      item.account_type || "",

      formatDate(item.demand_date),

      getStageLabel(item.current_status),

      item.pending_days || 0,

      item.payment_status || "",

      formatDate(item.last_action_date),

      item.last_action_remarks || "",
    ]);

    const csv = [headers, ...rows]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download = "procurement-register.csv";

    link.click();

    URL.revokeObjectURL(url);
  };

  /* =====================================================
     STAGE DATA FOR SELECTED CASE
  ===================================================== */

  const getStageHistory = (stageKey) => {
    if (!selectedCase) {
      return [];
    }

    return (selectedCase.history || [])
      .filter((history) => normalizeStage(history.status_stage) === stageKey)
      .sort((a, b) => {
        const dateA =
          new Date(a.action_date || a.created_at || 0).getTime() || 0;
        const dateB =
          new Date(b.action_date || b.created_at || 0).getTime() || 0;
        if (dateB !== dateA) return dateB - dateA;
        return Number(b.history_id || 0) - Number(a.history_id || 0);
      });
  };

  const getStageReminders = (stageKey) => {
    if (!selectedCase) {
      return [];
    }

    return (selectedCase.reminders || []).filter(
      (reminder) => normalizeStage(reminder.office_or_stage) === stageKey,
    );
  };

  const getStageDocuments = (stageKey) => {
    if (!selectedCase) {
      return [];
    }

    const histories = getStageHistory(stageKey);

    const historyIds = histories.map((item) => item.history_id);

    const reminders = getStageReminders(stageKey);

    const reminderIds = reminders.map((item) => item.reminder_id);

    return (selectedCase.documents || []).filter(
      (document) =>
        (document.history_id && historyIds.includes(document.history_id)) ||
        (document.reminder_id && reminderIds.includes(document.reminder_id)),
    );
  };

  /* =====================================================
     SELECT STAGE
  ===================================================== */

  const handleStageSelect = (stageKey) => {
    setSelectedStage(stageKey);
  };

  // After a timeline stage is selected, bring its history / reminder panel into view.
  useEffect(() => {
    if (!showDetails || !selectedStage) return;
    const timer = window.setTimeout(() => {
      stageDetailsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [selectedStage, showDetails]);

  // Latest recorded history date across all stages; used to highlight
  // the most recent date in the timeline.
  const latestTimelineTimestamp = (selectedCase?.history || [])
    .map((entry) =>
      new Date(entry.action_date || entry.created_at || 0).getTime(),
    )
    .filter((timestamp) => Number.isFinite(timestamp) && timestamp > 0)
    .reduce((latest, timestamp) => Math.max(latest, timestamp), 0);

  // When View opens the case, scroll the timeline panel to its current stage.
  useEffect(() => {
    if (!showDetails || !selectedCase) return;

    const container = timelineContainerRef.current;
    const currentItem = currentStageItemRef.current;
    if (!container || !currentItem) return;

    container.scrollTo({
      top: Math.max(0, currentItem.offsetTop - 70),
      behavior: "smooth",
    });
  }, [showDetails, selectedCase, selectedStage]);

  /* =====================================================
     CSS
  ===================================================== */

  const styles = `

    * {
      box-sizing:border-box;
    }

    .proc-page {
      min-height:100vh;
      padding:12px;
      background:
        linear-gradient(
          135deg,
          #e9faff,
          #eef7ff 50%,
          #f7efff
        );
      color:#202124;
    }

    .proc-container {
      max-width:1500px;
      margin:auto;
    }

    /* HEADER */

    .proc-header {
      min-height:100px;
      border-radius:18px;
      padding:22px 30px;
      color:white;
      background:
        linear-gradient(
          105deg,
          #c2185b,
          #d81b60 40%,
          #4a148c
        );
      display:flex;
      align-items:center;
      box-shadow:
        0 10px 30px
        rgba(91,27,120,.18);
    }

    .header-left {
      display:flex;
      align-items:center;
      gap:17px;
    }

    .header-icon {
      width:58px;
      height:58px;
      border-radius:14px;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:26px;
      background:
        rgba(255,255,255,.18);
    }

    .header-title {
      margin:0;
      font-size:27px;
      font-weight:700;
    }

    .header-subtitle {
      margin-top:5px;
      font-size:14px;
      opacity:.9;
    }

    /* SUMMARY */

    .summary-grid {
      display:grid;
      grid-template-columns:repeat(10, minmax(0, 1fr));
      gap:8px;
      margin-top:12px;
      align-items:stretch;
    }

    .summary-hover-wrap { position:relative; min-width:0; }
    .summary-card {
      appearance:none;
      width:100%;
      min-height:92px;
      aspect-ratio:1.15 / 1;
      display:flex;
      flex-direction:column;
      justify-content:space-between;
      text-align:left;
      color:inherit;
      font:inherit;
      cursor:pointer;
      background:white;
      border:1px solid #e3e7eb;
      border-radius:10px;
      padding:9px;
      box-shadow:0 3px 10px rgba(0,0,0,.04);
      transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease;
    }
    .summary-card:hover { border-color:#8bbcff; box-shadow:0 7px 16px rgba(20,116,232,.12); transform:translateY(-1px); }
    .summary-label { font-size:10px; line-height:1.25; font-weight:700; overflow-wrap:anywhere; }
    .summary-icon { width:25px; height:25px; border-radius:7px; font-size:12px; }
    .summary-number { font-size:22px; line-height:1.1; font-weight:800; margin-top:5px; }
    .summary-desc { font-size:9px; line-height:1.2; margin-top:3px; color:#777; }
    .kpi-hover-preview {
      position:absolute; top:calc(100% + 6px); left:0;
      width:min(300px, calc(100vw - 28px)); max-height:300px;
      overflow:auto; padding:10px; background:#fff;
      border:1px solid #cfd9e5; border-radius:10px;
      box-shadow:0 14px 35px rgba(15,23,42,.22);
      z-index:2500; color:#202124;
    }
    .summary-hover-wrap:nth-child(n + 9) .kpi-hover-preview { left:auto; right:0; }
    .kpi-hover-title { font-size:12px; font-weight:800; margin-bottom:8px; padding-bottom:7px; border-bottom:1px solid #e8edf3; }
    .kpi-hover-entry { padding:8px; border:1px solid #e4e9ef; border-radius:7px; margin-bottom:7px; background:#fbfdff; }
    .kpi-hover-entry:last-child { margin-bottom:0; }
    .kpi-hover-meta { display:flex; justify-content:space-between; gap:8px; font-size:10px; font-weight:700; margin-bottom:5px; }
    .kpi-hover-label { font-size:10px; font-weight:700; color:#687384; margin-top:4px; }
    .kpi-hover-text { font-size:11px; line-height:1.4; overflow-wrap:anywhere; }
    .kpi-hover-empty { font-size:11px; color:#707987; padding:10px 2px; }

    .summary-top {
      display:flex;
      justify-content:space-between;
      align-items:center;
    }

    .summary-label {
      font-size:10px;
      line-height:1.25;
      color:#5f6670;
      font-weight:700;
      overflow-wrap:anywhere;
    }

    .summary-icon {
      width:25px;
      height:25px;
      flex:0 0 25px;
      border-radius:7px;
      background:#eef4ff;
      color:#1474e8;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:12px;
    }

    .summary-number {
      font-size:22px;
      line-height:1.1;
      font-weight:800;
      margin-top:5px;
    }

    .summary-desc {
      font-size:9px;
      line-height:1.2;
      color:#777;
      margin-top:3px;
    }

    /* FILTER */

    .filter-card {
      margin-top:12px;
      padding:14px;
      background:white;
      border:1px solid #e1e5ea;
      border-radius:14px;
      display:grid;
      grid-template-columns:
        2fr 1fr 1fr 1fr 1fr 1fr auto;
      gap:10px;
    }

    .search-box {
      position:relative;
    }

    .search-box svg {
      position:absolute;
      left:13px;
      top:13px;
    }

    .input,
    .select {
      width:100%;
      height:43px;
      border:1px solid #d7dce2;
      border-radius:8px;
      padding:0 12px;
      background:white;
      outline:none;
    }

    .search-box .input {
      padding-left:38px;
    }

    .reset-btn,
    .export-btn {
      height:43px;
      padding:0 16px;
      border:1px solid #1475ef;
      color:#1475ef;
      background:white;
      border-radius:8px;
      font-weight:600;
      cursor:pointer;
    }

    /* TABLE */

    .table-card {
      margin-top:12px;
      background:white;
      border:1px solid #e0e5eb;
      border-radius:15px;
      overflow:hidden;
    }

    .table-top {
      padding:18px;
      display:flex;
      justify-content:space-between;
      align-items:center;
    }

    .table-title {
      font-size:17px;
      font-weight:700;
    }

    .table-heading-left { display:flex; align-items:center; gap:18px; flex-wrap:wrap; min-width:0; }
    .overall-status-bar { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
    .overall-status-chip {
      display:flex; align-items:center; gap:7px; padding:7px 11px;
      border:1px solid #dce3eb; border-radius:8px; background:#fff;
      color:#334155; font:inherit; cursor:pointer; white-space:nowrap;
      transition:background .15s ease, border-color .15s ease;
    }
    .overall-status-chip strong { font-size:13px; }
    .overall-status-chip span { font-size:11px; color:#64748b; }
    .overall-status-chip.active { box-shadow:0 0 0 2px rgba(20,116,232,.12); border-color:#1474e8; background:#eff6ff; }
    .overall-status-chip.in-progress strong { color:#1474e8; }
    .overall-status-chip.completed strong { color:#16844d; }
    .overall-status-chip.rejected strong { color:#d32323; }
    @media(max-width:900px) { .table-top { align-items:flex-start; gap:12px; flex-wrap:wrap; } .table-heading-left { gap:10px; } }

    .table-wrapper {
      overflow-x:auto;
    }

    table {
      width:100%;
      min-width:1100px;
      border-collapse:collapse;
    }

    th {
      background:#f8fafc;
      padding:12px 10px;
      text-align:left;
      font-size:12px;
      border-top:1px solid #e1e5e9;
      border-bottom:1px solid #e1e5e9;
      white-space:nowrap;
    }

    td {
      padding:12px 10px;
      font-size:12px;
      border-bottom:1px solid #edf0f3;
      white-space:nowrap;
    }

    tbody tr {
      cursor:pointer;
      transition:.15s;
    }

    tbody tr:hover {
      background:#f4f8ff;
    }

    tbody tr.selected-row {
      background:#eaf3ff;
      box-shadow:
        inset 3px 0 #1474e8;
    }

    .case-id {
      color:#1474e8;
      font-weight:700;
    }

    .material {
      font-weight:600;
    }

    .account {
      padding:5px 8px;
      background:#eef1f4;
      border-radius:5px;
      font-size:10px;
      font-weight:600;
    }

    .stage {
      display:inline-block;
      padding:5px 9px;
      border-radius:6px;
      background:#eef1f4;
      font-size:10px;
      font-weight:700;
    }

    .stage.approval {
      background:#fff0d4;
      color:#a25a00;
    }

    .stage.accounts {
      background:#e3f0ff;
      color:#1266ca;
    }

    .stage.purchase {
      background:#dff8ff;
      color:#087e9c;
    }

    .stage.inspection {
      background:#fff0dd;
      color:#b76500;
    }

    .stage.receipt {
      background:#dff7e9;
      color:#18834f;
    }

    .stage.completed {
      background:#d9f6e5;
      color:#16844d;
    }

    .stage.requirement {
      background:#eee9ff;
      color:#6b28c9;
    }

    .pending {
      padding:5px 8px;
      background:#f1f3f5;
      border-radius:5px;
      font-weight:600;
    }

    .pending.high {
      color:#d52d2d;
      background:#ffe3e3;
    }

    .status {
      padding:5px 8px;
      border-radius:6px;
      font-size:10px;
      font-weight:700;
      background:#e5f1ff;
      color:#1269d3;
    }

    .status.completed {
      background:#dcf7e7;
      color:#16844d;
    }

    .status.rejected {
      background:#ffe1e1;
      color:#d32323;
    }

    .view-btn {
      border:1px solid #6ca7f4;
      color:#1474e8;
      background:white;
      padding:6px 11px;
      border-radius:6px;
      cursor:pointer;
      font-weight:600;
    }

    .empty {
      text-align:center;
      padding:60px !important;
      color:#777;
    }

    .error {
      color:#d52d2d;
    }

    .table-footer {
      padding:14px 18px;
      display:flex;
      justify-content:space-between;
      align-items:center;
      font-size:12px;
      color:#6c737b;
    }

    .pagination {
      display:flex;
      gap:5px;
    }

    .page {
      width:34px;
      height:34px;
      background:white;
      border:1px solid #d8dde3;
      border-radius:6px;
      cursor:pointer;
    }

    .page.active {
      color:#1474e8;
      border-color:#1474e8;
      background:#f1f7ff;
    }


    /* =====================================================
       FULL SCREEN DETAIL POPUP
    ===================================================== */

    .detail-overlay {
      position:fixed;
      inset:0;
      background:
        rgba(15,23,42,.48);
      backdrop-filter:blur(3px);
      z-index:2000;
      padding:20px;
      overflow:auto;
    }

    .detail-modal {
      width:min(1250px,100%);
      min-height:calc(100vh - 40px);
      margin:auto;
      background:#f6f8fb;
      border-radius:16px;
      box-shadow:
        0 25px 70px
        rgba(0,0,0,.25);
      overflow:hidden;
    }

    .detail-header {
      height:70px;
      background:white;
      border-bottom:1px solid #e1e6ec;
      padding:0 22px;
      display:flex;
      justify-content:space-between;
      align-items:center;
      position:sticky;
      top:0;
      z-index:20;
    }

    .detail-header-title {
      font-size:18px;
      font-weight:700;
    }

    .detail-close {
      width:36px;
      height:36px;
      border:0;
      background:#f2f4f7;
      border-radius:50%;
      cursor:pointer;
      display:flex;
      align-items:center;
      justify-content:center;
    }

    .detail-layout {
      display:grid;
      grid-template-columns:
        1fr 1.15fr;
      gap:14px;
      padding:14px;
    }

    .detail-left,
    .detail-right {
      min-width:0;
    }

    .detail-card {
      background:white;
      border:1px solid #e1e6ec;
      border-radius:11px;
      padding:14px;
      margin-bottom:12px;
    }

    .detail-card-title {
      font-size:13px;
      font-weight:700;
      margin-bottom:13px;
      display:flex;
      align-items:center;
      gap:7px;
    }

    .item-header {
      display:flex;
      gap:12px;
      align-items:center;
    }

    .item-icon-large {
      width:54px;
      height:54px;
      border-radius:12px;
      background:#eaf2ff;
      color:#176dd9;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:23px;
    }

    .item-title {
      font-size:20px;
      font-weight:700;
    }

    .item-subtitle {
      color:#727a84;
      font-size:11px;
      margin-top:4px;
    }

    .current-position {
      background:#fffaf0;
      border:1px solid #f0dfad;
      border-radius:9px;
      padding:12px;
      margin-top:13px;
    }

    .current-position-title {
      display:flex;
      justify-content:space-between;
      align-items:center;
      font-size:12px;
      font-weight:700;
      margin-bottom:10px;
    }

    .position-row {
      display:grid;
      grid-template-columns:1fr 1.4fr;
      padding:4px 0;
      font-size:11px;
    }

    .position-label {
      color:#737b85;
    }

    .position-value {
      font-weight:600;
      text-align:right;
    }

    .danger {
      color:#db3030;
    }

    .info-grid {
      display:grid;
      grid-template-columns:
        1fr 1fr;
      gap:13px 18px;
    }

    .info-label {
      color:#7a828c;
      font-size:10px;
    }

    .info-value {
      font-size:11px;
      font-weight:600;
      margin-top:3px;
      word-break:break-word;
    }


    /* =====================================================
       VERTICAL TIMELINE
    ===================================================== */

    .timeline-card {
      background:white;
      border:1px solid #e1e6ec;
      border-radius:11px;
      padding:16px;
      margin-bottom:12px;
    }

    .timeline-title {
      font-size:14px;
      font-weight:700;
      margin-bottom:18px;
      display:flex;
      align-items:center;
      gap:7px;
    }

    .vertical-timeline {
      position:relative;
      padding-left:35px;
      max-height:calc(100vh - 260px);
      min-height:180px;
      overflow-y:auto;
      overflow-x:hidden;
      padding-right:8px;
      overscroll-behavior:contain;
      scroll-behavior:smooth;
    }

    .vertical-timeline::before {
      content:"";
      position:absolute;
      left:10px;
      top:8px;
      bottom:8px;
      width:2px;
      background:#d9e0e8;
    }

    .timeline-item {
      position:relative;
      min-height:65px;
      cursor:pointer;
      padding-bottom:16px;
    }

    .timeline-dot {
      position:absolute;
      left:-32px;
      top:2px;
      width:18px;
      height:18px;
      border-radius:50%;
      border:2px solid #9ca8b5;
      background:white;
      z-index:2;
    }

    .timeline-item.reached
    .timeline-dot {
      background:#1477ed;
      border-color:#1477ed;
    }

    .timeline-item.current
    .timeline-dot {
      background:#1477ed;
      border-color:#1477ed;
      box-shadow:
        0 0 0 5px #dcebff;
    }

    .timeline-item.selected
    .timeline-dot {
      background:#f59e0b;
      border-color:#f59e0b;
      box-shadow:
        0 0 0 5px #fff1cf;
    }

    .timeline-content {
      padding:8px 10px;
      border-radius:8px;
      transition:.15s;
    }

    .timeline-item:hover
    .timeline-content {
      background:#f5f8fc;
    }

    .timeline-item.selected
    .timeline-content {
      background:#fff8e9;
      border:1px solid #f3dfad;
    }

    .timeline-stage {
      display:flex;
      justify-content:space-between;
      gap:10px;
      font-size:12px;
      font-weight:700;
    }

    .timeline-date {
      display:table;
      position:relative;
      font-size:14px;
      line-height:1.5;
      font-weight:600;
      color:#596575;
      margin-top:6px;
      padding:3px 9px;
      border-radius:7px;
      isolation:isolate;
    }

    .timeline-date.latest-date {
      color:#135fc3;
      background:#eef6ff;
      border:1px solid #b9d7ff;
      z-index:0;
    }

    .timeline-date.latest-date::after {
      content:"";
      position:absolute;
      inset:-4px;
      border:2px solid #2384ff;
      border-radius:10px;
      pointer-events:none;
      animation:latestDateRing 1.6s ease-out infinite;
    }

    @keyframes latestDateRing {
      0% {
        transform:scale(.96);
        opacity:.95;
      }
      70%, 100% {
        transform:scale(1.08);
        opacity:0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .timeline-date.latest-date::after {
        animation:none;
      }
    }

    .current-badge {
      padding:3px 7px;
      background:#e5f1ff;
      color:#1268ce;
      border-radius:5px;
      font-size:9px;
    }


    /* =====================================================
       STAGE RELATED INFORMATION
    ===================================================== */

    .stage-detail {
      background:white;
      border:1px solid #e1e6ec;
      border-radius:11px;
      padding:15px;
    }

    .stage-detail-header {
      display:flex;
      justify-content:space-between;
      align-items:center;
      margin-bottom:14px;
    }

    .stage-detail-title {
      font-size:15px;
      font-weight:700;
    }

    .stage-count {
      font-size:10px;
      color:#777;
    }

    .related-section {
      margin-top:15px;
    }

    .related-section-title {
      display:flex;
      align-items:center;
      gap:7px;
      font-size:12px;
      font-weight:700;
      margin-bottom:9px;
    }

    .related-empty {
      font-size:11px;
      color:#888;
      background:#f7f8fa;
      border-radius:7px;
      padding:10px;
    }

    .history-box,
    .reminder-box,
    .document-box,
    .remark-box {
      border:1px solid #e5e9ee;
      border-radius:8px;
      padding:9px;
      margin-bottom:7px;
      background:#fff;
    }

    .history-top,
    .reminder-top {
      display:flex;
      justify-content:space-between;
      gap:10px;
      font-size:10px;
      font-weight:700;
    }

    .small-text {
      font-size:10px;
      color:#656d76;
      margin-top:4px;
      line-height:1.5;
    }

    .document-row {
      display:flex;
      align-items:center;
      gap:9px;
    }

    .document-icon {
      color:#dc3030;
      font-size:18px;
    }

    .document-name {
      font-size:10px;
      font-weight:700;
      word-break:break-all;
    }

    .document-meta {
      font-size:9px;
      color:#777;
      margin-top:3px;
    }

    .document-open {
      margin-left:auto;
      color:#1474e8;
      font-size:10px;
      text-decoration:none;
      white-space:nowrap;
    }

    .remark-box {
      background:#fffaf0;
      border-color:#f1dfb0;
    }

    .remark-label {
      font-size:9px;
      color:#8a7a53;
    }

    .remark-text {
      font-size:11px;
      margin-top:4px;
      font-weight:600;
    }



    /* =====================================================
       QUERY / REMINDER / ATTACHED DOCUMENTS
    ===================================================== */

    .query-box {
      border:1px solid #e2e7ed;
      border-radius:10px;
      padding:11px;
      margin-bottom:10px;
      background:#fff;
    }

    .query-header {
      display:flex;
      justify-content:space-between;
      align-items:center;
      gap:10px;
      padding-bottom:9px;
      border-bottom:1px solid #edf0f3;
    }

    .query-title {
      display:flex;
      align-items:center;
      gap:7px;
    }

    .query-number {
      background:#eaf3ff;
      color:#146ed8;
      padding:4px 8px;
      border-radius:5px;
      font-size:10px;
      font-weight:700;
    }

    .query-reminder-number {
      font-size:10px;
      font-weight:600;
      color:#555;
    }

    .query-date {
      font-size:10px;
      color:#666;
      font-weight:600;
      white-space:nowrap;
    }

    .query-matter {
      margin-top:10px;
    }

    .query-label {
      font-size:9px;
      color:#7a828c;
      font-weight:600;
      margin-bottom:4px;
    }

    .query-text {
      background:#fffaf0;
      border:1px solid #f1dfb0;
      border-radius:7px;
      padding:8px;
      font-size:10px;
      color:#414141;
      line-height:1.5;
    }

    .query-response {
      display:grid;
      grid-template-columns:135px 1fr;
      gap:8px;
      margin-top:9px;
    }

    .response-date-box,
    .response-text-box {
      border:1px solid #dce8f4;
      background:#f7fbff;
      border-radius:7px;
      padding:8px;
    }

    .response-date {
      font-size:10px;
      font-weight:600;
      color:#27658e;
    }

    .response-text {
      font-size:10px;
      color:#344b5b;
      line-height:1.5;
    }

    .query-documents {
      margin-top:10px;
      padding-top:9px;
      border-top:1px dashed #dfe4e9;
    }

    .query-documents > .query-label {
      display:flex;
      align-items:center;
      gap:5px;
    }

    .query-documents .document-box {
      margin-top:6px;
    }

    .query-no-document {
      font-size:9px;
      color:#8a929b;
      padding:6px 0;
    }

    .document-remark {
      margin-top:6px;
      padding-top:6px;
      border-top:1px solid #edf0f3;
      font-size:9px;
      color:#68717b;
    }

    @media(max-width:900px) {

      .summary-grid {
        grid-template-columns:repeat(5, minmax(0, 1fr));
      }

      .filter-card {
        grid-template-columns:
          repeat(3,1fr);
      }

      .detail-layout {
        grid-template-columns:1fr;
      }

    }


    @media(max-width:650px) {

      .query-header {
        align-items:flex-start;
      }

      .query-response {
        grid-template-columns:1fr;
      }



      .summary-grid {
        grid-template-columns:repeat(3, minmax(0, 1fr));
      }
      .summary-card { min-height:82px; padding:7px; }
      .summary-label { font-size:9px; }
      .summary-number { font-size:19px; }

      .filter-card {
        grid-template-columns:1fr;
      }

      .detail-overlay {
        padding:0;
      }

      .detail-modal {
        min-height:100vh;
        border-radius:0;
      }

      .info-grid {
        grid-template-columns:1fr;
      }

    }

  `;

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      <style>{styles}</style>

      <div className="proc-page">
        <div className="proc-container">
          {/* ================= HEADER ================= */}

          <div className="proc-header">
            <div className="header-left">
              <div className="header-icon">
                <FaFileAlt />
              </div>

              <div>
                <h1 className="header-title">Procurement Dashboard</h1>

                <div className="header-subtitle">
                  Track and monitor procurement cases from requirement to
                  completion
                </div>
              </div>
            </div>
          </div>

          {/* ================= SUMMARY ================= */}

          <div className="summary-grid">
            {STAGES.map((stage) => {
              const StageIcon = stage.icon;
              return (
                <SummaryCard
                  key={stage.key}
                  icon={<StageIcon />}
                  label={stage.key}
                  number={summary[stage.key] || 0}
                  desc="Current cases"
                  previewItems={getKpiPreviewItems(stage.key)}
                />
              );
            })}
          </div>

          {/* ================= FILTER ================= */}

          <div className="filter-card">
            <div className="search-box">
              <FaSearch />

              <input
                className="input"
                placeholder="Search case, material, file, e-office, demand..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);

                  setCurrentPage(1);
                }}
              />
            </div>

            <select
              className="select"
              value={accountFilter}
              onChange={(e) => {
                setAccountFilter(e.target.value);

                setCurrentPage(1);
              }}
            >
              {accountTypes.map((account) => (
                <option key={account} value={account}>
                  {account}
                </option>
              ))}
            </select>

            <select
              className="select"
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value);

                setCurrentPage(1);
              }}
            >
              <option value="All">All Stages</option>

              {STAGES.map((stage) => (
                <option key={stage.key} value={stage.key}>
                  {stage.label}
                </option>
              ))}
            </select>

            <input
              className="input"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);

                setCurrentPage(1);
              }}
            />

            <input
              className="input"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);

                setCurrentPage(1);
              }}
            />

            <select
              className="select"
              value={pendingFilter}
              onChange={(e) => {
                setPendingFilter(e.target.value);

                setCurrentPage(1);
              }}
            >
              <option value="All">Pending Days</option>

              <option value="0">0 Days</option>

              <option value="1-7">1–7 Days</option>

              <option value="8-15">8–15 Days</option>

              <option value="15+">15+ Days</option>
            </select>

            <button className="reset-btn" onClick={resetFilters}>
              <FaRedo /> Reset
            </button>
          </div>

          {/* ================= TABLE ================= */}

          <div className="table-card">
            <div className="table-top">
              <div className="table-heading-left">
                <div className="table-title">
                  Procurement Cases ({filteredCases.length})
                </div>
                <div
                  className="overall-status-bar"
                  aria-label="Filter procurement cases by overall status"
                >
                  <button
                    type="button"
                    className={`overall-status-chip in-progress ${statusFilter === "In Progress" ? "active" : ""}`}
                    aria-pressed={statusFilter === "In Progress"}
                    onClick={() => handleOverallStatusFilter("In Progress")}
                  >
                    <strong>{overallStatusCounts["In Progress"]}</strong>
                    <span>In Progress</span>
                  </button>
                  <button
                    type="button"
                    className={`overall-status-chip completed ${statusFilter === "Completed" ? "active" : ""}`}
                    aria-pressed={statusFilter === "Completed"}
                    onClick={() => handleOverallStatusFilter("Completed")}
                  >
                    <strong>{overallStatusCounts.Completed}</strong>
                    <span>Completed</span>
                  </button>
                  <button
                    type="button"
                    className={`overall-status-chip rejected ${statusFilter === "Rejected" ? "active" : ""}`}
                    aria-pressed={statusFilter === "Rejected"}
                    onClick={() => handleOverallStatusFilter("Rejected")}
                  >
                    <strong>{overallStatusCounts.Rejected}</strong>
                    <span>Rejected</span>
                  </button>
                </div>
              </div>

              <button className="export-btn" onClick={exportCSV}>
                <FaDownload /> Export
              </button>
            </div>

            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Case ID</th>
                    <th>Material Name</th>
                    <th>Qty</th>
                    <th>Account Type</th>
                    <th>Demand Date</th>
                    <th>Current Stage</th>
                    <th>Pending Days</th>
                    <th>Last Action Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="empty">
                        Loading procurement cases...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="11" className="empty error">
                        <FaExclamationTriangle />

                        <div>{error}</div>

                        <button
                          className="reset-btn"
                          onClick={fetchProcurements}
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  ) : visibleCases.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="empty">
                        <FaBoxOpen />

                        <div>No procurement cases found</div>
                      </td>
                    </tr>
                  ) : (
                    visibleCases.map((item, index) => {
                      const status = getOverallStatus(item);

                      const isSelected = selectedCase?.case_id === item.case_id;

                      return (
                        <tr
                          key={item.case_id}
                          className={isSelected ? "selected-row" : ""}
                          onClick={() => selectCase(item.case_id)}
                        >
                          <td>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>

                          <td>
                            <span className="case-id">{item.case_id}</span>
                          </td>

                          <td>
                            <span className="material">
                              {displayValue(item.item_name)}
                            </span>
                          </td>

                          <td>
                            {displayValue(item.quantity)}

                            {item.unit && ` ${item.unit}`}
                          </td>

                          <td>
                            <span className="account">
                              {displayValue(item.account_type)}
                            </span>
                          </td>

                          <td>{formatDate(item.demand_date)}</td>

                          <td>
                            <span
                              className={`
                                  stage
                                  ${normalizeStage(
                                    item.current_status,
                                  ).toLowerCase()}
                                `}
                            >
                              {getStageLabel(item.current_status)}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`
                                  pending
                                  ${Number(item.pending_days) > 7 ? "high" : ""}
                                `}
                            >
                              {item.pending_days || 0} days
                            </span>
                          </td>

                          <td>{formatDate(item.last_action_date)}</td>

                          <td>
                            <span
                              className={`
                                  status
                                  ${
                                    status === "Completed"
                                      ? "completed"
                                      : status === "Rejected"
                                        ? "rejected"
                                        : ""
                                  }
                                `}
                            >
                              {status}
                            </span>
                          </td>

                          <td>
                            <button
                              className="view-btn"
                              onClick={(e) => {
                                e.stopPropagation();

                                selectCase(item.case_id, true);
                              }}
                            >
                              <FaEye /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div>
                Showing{" "}
                {filteredCases.length
                  ? (currentPage - 1) * itemsPerPage + 1
                  : 0}{" "}
                to {Math.min(currentPage * itemsPerPage, filteredCases.length)}{" "}
                of {filteredCases.length} cases
              </div>

              <div className="pagination">
                <button
                  className="page"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <FaChevronLeft />
                </button>

                <button className="page active">{currentPage}</button>

                <button
                  className="page"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          FULL SCREEN DETAIL POPUP
      ===================================================== */}

      {showDetails && selectedCase && (
        <div className="detail-overlay" onClick={() => setShowDetails(false)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            {/* POPUP HEADER */}

            <div className="detail-header">
              <div className="detail-header-title">
                Procurement Case #{selectedCase.case_id}
              </div>

              <button
                className="detail-close"
                onClick={() => setShowDetails(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="detail-layout">
              {/* =================================================
                  LEFT SIDE - GENERAL INFORMATION
              ================================================= */}

              <div className="detail-left">
                {/* ITEM */}

                <div className="detail-card">
                  <div className="item-header">
                    <div className="item-icon-large">
                      <FaBoxOpen />
                    </div>

                    <div>
                      <div className="item-title">
                        {displayValue(selectedCase.item_name)}
                      </div>

                      <div className="item-subtitle">
                        Case ID: {selectedCase.case_id}
                        {" | "}
                        Item ID: {selectedCase.item_id}
                      </div>

                      <div className="item-subtitle">
                        Account: {displayValue(selectedCase.account_type)}
                      </div>

                      <div className="item-subtitle">
                        Quantity: {displayValue(selectedCase.quantity)}{" "}
                        {displayValue(selectedCase.unit)}
                      </div>
                    </div>
                  </div>

                  {/* CURRENT POSITION */}

                  <div className="current-position">
                    <div className="current-position-title">
                      <span>
                        <FaTools /> Current Position
                      </span>

                      <span
                        className={`stage ${normalizeStage(
                          selectedCase.current_status,
                        ).toLowerCase()}`}
                      >
                        {getStageLabel(selectedCase.current_status)}
                      </span>
                    </div>

                    <PositionRow
                      label="Pending Since"
                      value={formatDate(selectedCase.pending_since)}
                    />

                    <PositionRow
                      label="Pending Days"
                      value={`${selectedCase.pending_days || 0} days`}
                      danger={Number(selectedCase.pending_days) > 7}
                    />

                    <PositionRow
                      label="Last Action Date"
                      value={formatDate(selectedCase.last_action_date)}
                    />

                    <PositionRow
                      label="Last Action Stage"
                      value={getStageLabel(selectedCase.last_action_stage)}
                    />

                    <PositionRow
                      label="Last Action Remarks"
                      value={displayValue(selectedCase.last_action_remarks)}
                    />
                  </div>
                </div>

                {/* BASIC INFORMATION */}

                <InfoCard
                  title="Basic Information"
                  icon={<FaInfoCircle />}
                  fields={[
                    ["Case ID", selectedCase.case_id],
                    ["File Number", selectedCase.file_number],
                    ["e-Office Number", selectedCase.e_office_number],
                    [
                      "Intimation Date",
                      formatDate(selectedCase.intimation_date),
                    ],
                    ["Demand Number", selectedCase.demand_number],
                    ["Item ID", selectedCase.item_id],
                    ["Item Name", selectedCase.item_name],
                    ["Quantity", selectedCase.quantity],
                    ["Unit", selectedCase.unit],
                    ["Account Type", selectedCase.account_type],
                    ["Demand Date", formatDate(selectedCase.demand_date)],
                  ]}
                />

                {/* PROCUREMENT DETAILS */}

                <InfoCard
                  title="Procurement Details"
                  icon={<FaShoppingCart />}
                  fields={[
                    ["Indent Date", formatDate(selectedCase.indent_date)],
                    [
                      "UDM Entry Date",
                      formatDate(selectedCase.demand_entry_date_udm),
                    ],
                    ["Approval Date", formatDate(selectedCase.approval_date)],
                    [
                      "Finance Concurrence",
                      formatDate(selectedCase.finance_concurrence_date),
                    ],
                    [
                      "Fund Availability",
                      formatDate(selectedCase.fund_availability_date),
                    ],
                    ["Finance Vetting", selectedCase.finance_vetting_status],
                    ["Sanction Date", formatDate(selectedCase.sanction_date)],
                    ["PO Number", selectedCase.purchase_order_number],
                    ["PO Date", formatDate(selectedCase.purchase_order_date)],
                    ["Ordered Qty", selectedCase.ordered_qty],
                  ]}
                />

                {/* INSPECTION */}

                <InfoCard
                  title="Inspection & Receipt"
                  icon={<FaBoxOpen />}
                  fields={[
                    [
                      "Inspection Date",
                      formatDate(selectedCase.inspection_date),
                    ],
                    ["Inspection Result", selectedCase.inspection_result],
                    ["Received Date", formatDate(selectedCase.received_date)],
                    ["Received Qty", selectedCase.received_qty],
                    ["Accepted Qty", selectedCase.accepted_qty],
                    ["Rejected Qty", selectedCase.rejected_qty],
                    [
                      "Rejection Date",
                      formatDate(selectedCase.item_rejection_date),
                    ],
                    ["Rejection Reason", selectedCase.rejection_reason],
                    // TPI details are stored in sample_inspections by the TS&RR API.
                    [
                      "TPI",
                      (selectedCase.TPI ??
                      selectedCase.tpi ??
                      (Array.isArray(selectedCase.sample_inspections)
                        ? selectedCase.sample_inspections.some((sample) =>
                            Boolean(
                              sample?.TPI ||
                              sample?.TPI_agency ||
                              sample?.TPI_date ||
                              sample?.TPI_result ||
                              sample?.TPI_remark,
                            ),
                          )
                        : false))
                        ? "Enabled"
                        : "No",
                    ],
                    [
                      "TPI Agency",
                      selectedCase.TPI_agency ||
                        selectedCase.tpi_agency ||
                        (Array.isArray(selectedCase.sample_inspections)
                          ? [...selectedCase.sample_inspections].sort(
                              (a, b) =>
                                new Date(
                                  b?.created_at || b?.submitted_date || 0,
                                ).getTime() -
                                new Date(
                                  a?.created_at || a?.submitted_date || 0,
                                ).getTime(),
                            )[0]?.TPI_agency
                          : null),
                    ],
                    [
                      "TPI Date",
                      formatDate(
                        selectedCase.TPI_date ||
                          selectedCase.tpi_date ||
                          (Array.isArray(selectedCase.sample_inspections)
                            ? [...selectedCase.sample_inspections].sort(
                                (a, b) =>
                                  new Date(
                                    b?.created_at || b?.submitted_date || 0,
                                  ).getTime() -
                                  new Date(
                                    a?.created_at || a?.submitted_date || 0,
                                  ).getTime(),
                              )[0]?.TPI_date
                            : null),
                      ),
                    ],
                    [
                      "TPI Result",
                      selectedCase.TPI_result ||
                        selectedCase.tpi_result ||
                        (Array.isArray(selectedCase.sample_inspections)
                          ? [...selectedCase.sample_inspections].sort(
                              (a, b) =>
                                new Date(
                                  b?.created_at || b?.submitted_date || 0,
                                ).getTime() -
                                new Date(
                                  a?.created_at || a?.submitted_date || 0,
                                ).getTime(),
                            )[0]?.TPI_result
                          : null),
                    ],
                    [
                      "TPI Remark",
                      selectedCase.TPI_remark ||
                        selectedCase.tpi_remark ||
                        (Array.isArray(selectedCase.sample_inspections)
                          ? [...selectedCase.sample_inspections].sort(
                              (a, b) =>
                                new Date(
                                  b?.created_at || b?.submitted_date || 0,
                                ).getTime() -
                                new Date(
                                  a?.created_at || a?.submitted_date || 0,
                                ).getTime(),
                            )[0]?.TPI_remark
                          : null),
                    ],
                  ]}
                />

                {/* PAYMENT */}

                <InfoCard
                  title="Payment"
                  icon={<FaMoneyCheckAlt />}
                  fields={[
                    ["Payment Type", selectedCase.payment_type],
                    ["Payment Status", selectedCase.payment_status],
                  ]}
                />
              </div>

              {/* =================================================
                  RIGHT SIDE - TIMELINE
              ================================================= */}

              <div className="detail-right">
                {/* VERTICAL TIMELINE */}

                <div className="timeline-card">
                  <div className="timeline-title">
                    <FaHistory />
                    Procurement Stage Timeline
                  </div>

                  <div className="vertical-timeline" ref={timelineContainerRef}>
                    {STAGES.map((stage, index) => {
                      const currentStage = getCurrentStage(selectedCase);

                      const currentIndex = STAGES.findIndex(
                        (s) => s.key === currentStage,
                      );

                      const reached = index <= currentIndex;

                      const selected = selectedStage === stage.key;

                      const histories = getStageHistory(stage.key);

                      const stageDate = histories.length
                        ? histories[0].action_date || histories[0].created_at
                        : null;

                      const stageDateTimestamp = stageDate
                        ? new Date(stageDate).getTime()
                        : 0;

                      const isLatestDate =
                        stageDateTimestamp > 0 &&
                        stageDateTimestamp === latestTimelineTimestamp;

                      return (
                        <div
                          key={stage.key}
                          ref={
                            currentStage === stage.key
                              ? currentStageItemRef
                              : null
                          }
                          className={`
                              timeline-item
                              ${reached ? "reached" : ""}
                              ${currentStage === stage.key ? "current" : ""}
                              ${selected ? "selected" : ""}
                            `}
                          onClick={() => handleStageSelect(stage.key)}
                        >
                          <div className="timeline-dot" />

                          <div className="timeline-content">
                            <div className="timeline-stage">
                              <span>{stage.label}</span>

                              {currentStage === stage.key && (
                                <span className="current-badge">
                                  Current Stage
                                </span>
                              )}
                            </div>

                            <div
                              className={`timeline-date ${
                                isLatestDate ? "latest-date" : ""
                              }`}
                              aria-label={
                                isLatestDate
                                  ? `Latest timeline date: ${formatDate(stageDate)}`
                                  : undefined
                              }
                            >
                              {stageDate ? formatDate(stageDate) : "—"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* =================================================
                    SELECTED STAGE DETAILS
                ================================================= */}

                {selectedStage && (
                  <div
                    ref={stageDetailsRef}
                    className="stage-details-scroll-target"
                  >
                    <StageDetails
                      stageKey={selectedStage}
                      selectedCase={selectedCase}
                      getStageHistory={getStageHistory}
                      getStageReminders={getStageReminders}
                      getStageDocuments={getStageDocuments}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* =========================================================
   SUMMARY CARD
========================================================= */

function SummaryCard({ icon, label, number, desc, previewItems = [] }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="summary-hover-wrap"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        className="summary-card"
        aria-label={`${label}: hover to preview the 5 oldest-pending items`}
      >
        <div className="summary-top">
          <div className="summary-label">{label}</div>
          <div className="summary-icon">{icon}</div>
        </div>
        <div className="summary-number">{number}</div>
        <div className="summary-desc">{desc}</div>
      </button>

      {isHovered && (
        <div className="kpi-hover-preview" role="tooltip">
          <div className="kpi-hover-title">
            {label} · Item Name & Pending Since
          </div>
          {previewItems.length === 0 ? (
            <div className="kpi-hover-empty">
              No cases are currently at this stage.
            </div>
          ) : (
            previewItems.map((item, index) => (
              <div className="kpi-hover-entry" key={`${item.caseId}-${index}`}>
                <div className="kpi-hover-meta">
                  <span>Case #{item.caseId}</span>
                  <span>
                    {item.pendingDays != null
                      ? `${item.pendingDays} day(s)`
                      : ""}
                  </span>
                </div>
                <div className="kpi-hover-text">
                  <strong>Item:</strong> {displayValue(item.itemName)}
                </div>
                <div className="kpi-hover-label">Pending since</div>
                <div className="kpi-hover-text">
                  {formatDate(item.pendingSince)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   INFO CARD
========================================================= */

function InfoCard({ title, icon, fields }) {
  return (
    <div className="detail-card">
      <div className="detail-card-title">
        {icon}

        {title}
      </div>

      <div className="info-grid">
        {fields.map(([label, value], index) => (
          <div key={index}>
            <div className="info-label">{label}</div>

            <div className="info-value">{displayValue(value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   POSITION ROW
========================================================= */

function PositionRow({ label, value, danger }) {
  return (
    <div className="position-row">
      <span className="position-label">{label}</span>

      <span
        className={`
          position-value
          ${danger ? "danger" : ""}
        `}
      >
        {value}
      </span>
    </div>
  );
}

/* =========================================================
   STAGE DETAILS

   This is the important part.

   When user selects Approval:
      Approval history
      Approval reminders
      Approval documents
      Approval remarks

   When user selects Accounts:
      Accounts history
      Accounts reminders
      Accounts documents
      Accounts remarks
========================================================= */

function StageDetails({
  stageKey,
  selectedCase,
  getStageHistory,
  getStageReminders,
  getStageDocuments,
}) {
  const histories = getStageHistory(stageKey);
  const reminders = getStageReminders(stageKey);
  const documents = getStageDocuments(stageKey);
  const stageLabel = getStageLabel(stageKey);

  /*
     DOCUMENT ↔ QUERY RELATION

     Priority:
     1. Direct relation: document.reminder_id === reminder.reminder_id
     2. If the API does not send reminder_id on the document,
        use document.history_id and the history belonging to
        the selected stage.
     3. If there are multiple reminders for the same stage,
        use the closest reminder date to the document/history date
        as a safe frontend fallback.

     For the current API data:
       reminder 18 (APPROVAL)
          ↓
       history 60 (APPROVAL)
          ↓
       document 14
          ↓
       LCGATES_compressed_backup.pdf
  */

  const toTime = (value) => {
    if (!value) return null;

    const time = new Date(value).getTime();

    return Number.isNaN(time) ? null : time;
  };

  const getHistoryForDocument = (document) => {
    if (document?.history_id == null) {
      return null;
    }

    return (
      histories.find(
        (history) =>
          history.history_id != null &&
          String(history.history_id) === String(document.history_id),
      ) || null
    );
  };

  const getReminderDocuments = (reminder) => {
    /*
       1. Direct reminder_id relation.
    */
    const directDocuments = documents.filter(
      (document) =>
        document.reminder_id != null &&
        reminder.reminder_id != null &&
        String(document.reminder_id) === String(reminder.reminder_id),
    );

    if (directDocuments.length > 0) {
      return directDocuments;
    }

    /*
       2. If reminder has history_id, use it directly.
    */
    if (reminder.history_id != null) {
      return documents.filter(
        (document) =>
          document.history_id != null &&
          String(document.history_id) === String(reminder.history_id),
      );
    }

    /*
       3. Current API fallback:
          reminder does not have history_id, but document has it.
          Find documents whose history belongs to this stage.
    */
    const stageHistoryDocuments = documents.filter((document) =>
      histories.some(
        (history) =>
          history.history_id != null &&
          document.history_id != null &&
          String(history.history_id) === String(document.history_id),
      ),
    );

    if (stageHistoryDocuments.length === 0) {
      return [];
    }

    /*
       If this stage has only one reminder, the stage-history
       documents unambiguously belong to that reminder.
    */
    if (reminders.length === 1) {
      return stageHistoryDocuments;
    }

    /*
       If multiple reminders exist at the same stage,
       associate a document with the reminder closest to the
       document/history date.
    */
    return stageHistoryDocuments.filter((document) => {
      const documentHistory = getHistoryForDocument(document);

      const documentTime = toTime(
        document.created_at ||
          documentHistory?.created_at ||
          documentHistory?.action_date,
      );

      if (documentTime == null) {
        return false;
      }

      let closestReminder = null;
      let closestDifference = Infinity;

      reminders.forEach((item) => {
        const reminderTime = toTime(
          item.created_at || item.reminder_date || item.updated_at,
        );

        if (reminderTime == null) {
          return;
        }

        const difference = Math.abs(reminderTime - documentTime);

        if (difference < closestDifference) {
          closestDifference = difference;
          closestReminder = item;
        }
      });

      return (
        closestReminder?.reminder_id != null &&
        String(closestReminder.reminder_id) === String(reminder.reminder_id)
      );
    });
  };

  /*
     Documents already displayed inside a query should not be
     repeated again under generic "Stage Documents".
  */
  const queryDocumentIds = new Set();

  reminders.forEach((reminder) => {
    getReminderDocuments(reminder).forEach((document) => {
      if (document.document_id != null) {
        queryDocumentIds.add(String(document.document_id));
      }
    });
  });

  const otherDocuments = documents.filter(
    (document) =>
      document.document_id == null ||
      !queryDocumentIds.has(String(document.document_id)),
  );

  const DocumentCard = ({ document }) => (
    <div className="document-box">
      <div className="document-row">
        <FaFileAlt className="document-icon" />

        <div>
          <div className="document-name">
            {displayValue(document.original_file_name)}
          </div>

          <div className="document-meta">
            {displayValue(document.mime_type)}

            {" • "}

            {formatFileSize(document.file_size)}
          </div>
        </div>

        {document.download_url && (
          <a
            className="document-open"
            href={document.download_url}
            target="_blank"
            rel="noreferrer"
          >
            Open
          </a>
        )}
      </div>

      {document.remarks && (
        <div className="document-remark">{document.remarks}</div>
      )}
    </div>
  );

  return (
    <div className="stage-detail">
      <div className="stage-detail-header">
        <div className="stage-detail-title">{stageLabel} Details</div>

        <div className="stage-count">
          {histories.length} history
          {" • "}
          {reminders.length} queries
          {" • "}
          {documents.length} documents
        </div>
      </div>

      {/* HISTORY */}

      {histories.length > 0 && (
        <div className="related-section">
          <div className="related-section-title">
            <FaHistory />
            Stage History
          </div>

          {histories.map((history) => (
            <div className="history-box" key={history.history_id}>
              <div className="history-top">
                <span>{getStageLabel(history.status_stage)}</span>

                <span>{formatDate(history.action_date)}</span>
              </div>

              {history.remarks && (
                <div className="small-text">{history.remarks}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* QUERIES / REMINDERS */}

      <div className="related-section">
        <div className="related-section-title">
          <FaBell />
          Queries / Reminders
        </div>

        {reminders.length === 0 ? (
          <div className="related-empty">
            No queries or reminders for this stage.
          </div>
        ) : (
          reminders.map((reminder, index) => {
            const reminderDocuments = getReminderDocuments(reminder);

            return (
              <div className="query-box" key={reminder.reminder_id ?? index}>
                <div className="query-header">
                  <div className="query-title">
                    <span className="query-number">Query {index + 1}</span>

                    {reminder.reminder_number && (
                      <span className="query-reminder-number">
                        {displayValue(reminder.reminder_number)}
                      </span>
                    )}
                  </div>

                  <div className="query-date">
                    {formatDate(reminder.reminder_date)}
                  </div>
                </div>

                <div className="query-matter">
                  <div className="query-label">Reminder</div>

                  <div className="query-text">
                    {displayValue(reminder.remarks)}
                  </div>
                </div>

                <div className="query-response">
                  <div className="response-date-box">
                    <div className="query-label">Response Date</div>

                    <div className="response-date">
                      {reminder.response_date
                        ? formatDate(reminder.response_date)
                        : "Pending"}
                    </div>
                  </div>

                  <div className="response-text-box">
                    <div className="query-label">Response</div>

                    <div className="response-text">
                      {displayValue(reminder.response_remarks)}
                    </div>
                  </div>
                </div>

                <div className="query-documents">
                  <div className="query-label">
                    <FaPaperclip /> Attached Documents
                  </div>

                  {reminderDocuments.length === 0 ? (
                    <div className="query-no-document">
                      No document attached to this query.
                    </div>
                  ) : (
                    reminderDocuments.map((document) => (
                      <DocumentCard
                        key={document.document_id}
                        document={document}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* DOCUMENTS NOT LINKED TO A QUERY */}

      {otherDocuments.length > 0 && (
        <div className="related-section">
          <div className="related-section-title">
            <FaPaperclip />
            Stage Documents
          </div>

          {otherDocuments.map((document) => (
            <DocumentCard key={document.document_id} document={document} />
          ))}
        </div>
      )}
    </div>
  );
}

export default ProcurementOverview;
